import { useState } from "react";
import NumberInput from "../shared/NumberInput";
import RadioCardGroup from "../shared/RadioCardGroup";
import StepNavigation from "../shared/StepNavigation";
import { ROIFormData } from "./ROISimulator";

interface Props {
  data: ROIFormData;
  onChange: (data: Partial<ROIFormData>) => void;
  onNext: () => void;
}

type Ausrichtung = "sued" | "suedost" | "suedwest" | "ost" | "west" | "ost_west";

const AUSRICHTUNGEN = [
  { value: "sued" as Ausrichtung, label: "Süd", description: "Optimal – 100% Ertrag" },
  { value: "suedost" as Ausrichtung, label: "Süd-Ost", description: "95% Ertrag" },
  { value: "suedwest" as Ausrichtung, label: "Süd-West", description: "95% Ertrag" },
  { value: "ost_west" as Ausrichtung, label: "Ost-West", description: "90% Ertrag – gleichmäßige Erzeugung" },
  { value: "ost" as Ausrichtung, label: "Ost", description: "85% Ertrag" },
  { value: "west" as Ausrichtung, label: "West", description: "85% Ertrag" },
];

export default function Step1Dach({ data, onChange, onNext }: Props) {
  const [errors, setErrors] = useState<{
    dachflaeche_m2?: string;
    ausrichtung?: string;
    dachneigung?: string;
  }>({});

  const validate = () => {
    const e: typeof errors = {};
    if (!data.dachflaeche_m2 || data.dachflaeche_m2 < 10)
      e.dachflaeche_m2 = "Mindestens 10 m² Dachfläche erforderlich.";
    if (!data.ausrichtung) e.ausrichtung = "Bitte Ausrichtung auswählen.";
    if (data.dachneigung !== undefined && (data.dachneigung < 0 || data.dachneigung > 60))
      e.dachneigung = "Dachneigung zwischen 0° und 60°.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Berechne Vorschau der kWp (Basic 0.17 – Premium 0.21 kWp/m²)
  const kwpMin = data.dachflaeche_m2 ? (data.dachflaeche_m2 * 0.17).toFixed(1) : null;
  const kwpMax = data.dachflaeche_m2 ? (data.dachflaeche_m2 * 0.21).toFixed(1) : null;

  return (
    <div className="animate-slide-up" data-testid="step-1-dach">
      <NumberInput
        label="Nutzbare Dachfläche"
        placeholder="z. B. 40"
        unit="m²"
        min={10}
        max={500}
        value={data.dachflaeche_m2 ?? ""}
        onChange={(e) => onChange({ dachflaeche_m2: parseFloat(e.target.value) || undefined })}
        error={errors.dachflaeche_m2}
        hint={kwpMin && kwpMax ? `ab ${kwpMin} bis ca. ${kwpMax} kWp Anlagenleistung` : "Nutzbare Fläche ohne Gauben, Schornsteine etc."}
        data-testid="dachflaeche-input"
      />

      <RadioCardGroup
        label="Dachausrichtung"
        options={AUSRICHTUNGEN}
        value={data.ausrichtung}
        onChange={(v) => onChange({ ausrichtung: v })}
        error={errors.ausrichtung}
        testId="ausrichtung-group"
      />

      <NumberInput
        label="Dachneigung"
        placeholder="30"
        unit="°"
        min={0}
        max={60}
        value={data.dachneigung ?? ""}
        onChange={(e) => onChange({ dachneigung: parseInt(e.target.value) || 30 })}
        error={errors.dachneigung}
        hint="Optimal: 30–35°. Flachdach = 0°."
        data-testid="dachneigung-input"
      />

      <StepNavigation
        onNext={() => { if (validate()) onNext(); }}
        nextLabel="Zum Verbrauch"
      />
    </div>
  );
}
